import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
// Import ChevronDown for FAQ toggle
import { Search, Mail, ChevronDown } from 'lucide-react';
import guidesData from '../help/guides.json';
import { getYouTubeVideoId } from '../utils/youtubeUtils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const faqs = [
  {
    id: 'faq-1',
    question: 'How do I add or manage my dental practices?',
    answer: 'You can manage the practices you work at by clicking the settings (cog) icon next to the practice selection dropdown on the main invoice creation page. This allows you to add, remove, or update practice details.',
  },
  {
    id: 'faq-2',
    question: 'How do I generate an invoice?',
    answer: 'Navigate to the "Create Invoice" page (the default page after logging in). Select the practice, fill in the daily log entries for the desired period, and click "Generate Invoice". You can then preview, save, or send the invoice.',
  },
  {
    id: 'faq-3',
    question: 'How is my personal and financial data secured?',
    answer: 'We take data security very seriously. All sensitive information, such as your bank details and practice information, is encrypted both when stored in our database and when transmitted over the internet. We use industry-standard encryption protocols.',
  },
  {
    id: 'faq-4',
    question: 'Can I access my invoice history?',
    answer: 'Yes, Pro plan users can access their complete invoice history by navigating to the "Invoice History" section from the main menu. You can view, download, or resend past invoices.',
  },
  {
    id: 'faq-5',
    question: 'How does the billing work?',
    answer: 'We offer monthly and yearly subscription plans. You can manage your subscription, view billing history, and update payment methods in the "Billing" section, accessible from the user menu.',
  },
];

const SupportPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null); // For modal
  const [showArticleModal, setShowArticleModal] = useState(false);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    // Live search as user types
    performSearch(event.target.value);
  };

  // Updated search to work on all article fields and be more robust
  const performSearch = (term = searchTerm) => {
    const search = (term || '').trim().toLowerCase();
    if (!search) {
      setSearchResults([]);
      return;
    }

    const results = Object.entries(guidesData).map(([key, guide]) => {
      let score = 0;
      const matchedKeywords = new Set();

      // Search in title
      if (guide.title && guide.title.toLowerCase().includes(search)) {
        score += 5;
        matchedKeywords.add(search);
      }

      // Search in steps
      guide.steps.forEach(step => {
        if (step.title && step.title.toLowerCase().includes(search)) {
          score += 3;
          matchedKeywords.add(search);
        }
        if (step.description && step.description.toLowerCase().includes(search)) {
          score += 1;
          matchedKeywords.add(search);
        }
      });

      // Search in tips
      guide.tips.forEach(tip => {
        if (tip && tip.toLowerCase().includes(search)) {
          score += 1;
          matchedKeywords.add(search);
        }
      });

      return score > 0
        ? {
            key,
            ...guide,
            score,
            matchedKeywords: Array.from(matchedKeywords),
            videoId: getYouTubeVideoId(guide.youtubeLink),
            videoEmbedUrl: guide.youtubeLink ? `https://www.youtube.com/embed/${getYouTubeVideoId(guide.youtubeLink)}` : null
          }
        : null;
    }).filter(Boolean);

    results.sort((a, b) => b.score - a.score);
    setSearchResults(results);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      performSearch();
    }
  };

  const highlightMatches = (text, keywords) => {
    if (!keywords || keywords.length === 0) return text;
    const regex = new RegExp(`(${keywords.join('|')})`, 'gi');
    // Keep yellow highlight for visibility in both modes
    return text.split(regex).map((part, index) =>
      regex.test(part) ? <mark key={index} className="bg-yellow-300 text-black px-1 rounded">{part}</mark> : part
    );
  };

  const mailtoLink = 'mailto:support@coooly.co.uk?subject=Support Request - Hygienist Invoice App';

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  // Function to open article modal
  const openArticleModal = (article) => {
    setSelectedArticle(article);
    setShowArticleModal(true);
  };

  // Function to close article modal
  const closeArticleModal = () => {
    setShowArticleModal(false);
    setSelectedArticle(null);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Apply theme classes to the main card */}
      <Card className="max-w-4xl mx-auto app-card-bg app-card-border shadow-lg">
        <CardHeader>
          {/* Use theme text classes */}
          <CardTitle className="text-2xl font-bold text-accent-foreground">Help & Support</CardTitle>
          <p className="text-muted-foreground pt-1">
            Find answers to common questions or search our help guides.
          </p>
        </CardHeader>
        <CardContent className="space-y-8">

          {/* Search Section */}
          <div className="space-y-4">
            {/* Use theme text class */}
            <h3 className="text-xl font-semibold text-primary">Search Help Articles</h3>
            <div className="flex space-x-2">
              {/* Remove app-input-bg and app-input-border, rely on global override */}
              <Input
                type="text"
                placeholder="e.g., 'create invoice', 'add practice', 'tax'"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                // Keep text color classes
                className="text-primary placeholder:text-muted-foreground flex-grow" 
              />
              {/* Apply theme classes to Button */}
              <Button onClick={() => performSearch()} className="app-accent-purple-bg hover:app-accent-purple-hover text-white">
                <Search className="h-4 w-4 mr-2" /> Search
              </Button>
            </div>

            {/* Search Results */}
            <div className="mt-6 space-y-4">
              {searchResults.length > 0 ? (
                searchResults.map((result) => (
                  <div
                    key={result.key}
                    className="cursor-pointer"
                    onClick={() => openArticleModal(result)}
                  >
                    {/* Apply theme classes to result Card */}
                    <Card className="app-bg-secondary border-border hover:border-accent-foreground transition-colors">
                      <CardHeader>
                        {/* Use theme text class */}
                        <CardTitle className="text-lg text-accent-foreground">{highlightMatches(result.title, result.matchedKeywords)}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {result.steps?.slice(0, 2).map((step, index) => (
                          // Use theme text class
                          <p key={`step-${index}`} className="text-sm text-muted-foreground">
                            <strong className="text-primary">{highlightMatches(step.title, result.matchedKeywords)}</strong> - {highlightMatches(step.description, result.matchedKeywords)}
                          </p>
                        ))}
                        {/* Use theme text class */}
                        {result.steps?.length > 2 && <p className="text-sm text-muted-foreground italic">... and more steps.</p>}
                        {result.tips?.some(tip => result.matchedKeywords.some(kw => tip.toLowerCase().includes(kw.toLowerCase()))) && (
                          // Use theme border class
                          <div className="pt-2 border-t border-border/50 mt-3">
                            {/* Use theme text classes */}
                            <p className="text-sm font-semibold text-muted-foreground">Relevant Tips:</p>
                            <ul className="list-disc list-inside text-sm text-muted-foreground">
                              {result.tips
                                .filter(tip => result.matchedKeywords.some(kw => tip.toLowerCase().includes(kw.toLowerCase())))
                                .map((tip, index) => (
                                  <li key={`tip-${index}`}>{highlightMatches(tip, result.matchedKeywords)}</li>
                                ))}
                            </ul>
                          </div>
                        )}
                        {result.videoEmbedUrl && (
                          // Use theme border and text classes
                          <div className="pt-3 border-t border-border/50 mt-3">
                            <h4 className="font-semibold text-primary mb-2 text-sm">Related Video:</h4>
                            <div className="aspect-video max-w-sm">
                              <iframe
                                width="100%"
                                height="100%"
                                src={result.videoEmbedUrl}
                                title="YouTube video player"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="rounded"
                              ></iframe>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ))
              ) : (
                 // Use theme text class
                searchTerm.trim() && <p className="text-muted-foreground text-center">No matching help articles found.</p>
              )}
            </div>
          </div>

          {/* Article Modal */}
          <Dialog open={showArticleModal} onOpenChange={setShowArticleModal}>
            {/* Apply theme classes to DialogContent */}
            <DialogContent className="sm:max-w-[600px] app-card-bg app-card-border text-primary max-h-[85vh] flex flex-col">
              {selectedArticle && (
                <>
                  <DialogHeader>
                    {/* Use theme text class */}
                    <DialogTitle className="text-accent-foreground text-xl">{selectedArticle.title}</DialogTitle>
                  </DialogHeader>
                  {/* Use theme text classes */}
                  <div className="py-4 space-y-4 overflow-y-auto flex-grow pr-6">
                    {selectedArticle.steps && selectedArticle.steps.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-primary mb-2">Steps:</h4>
                        <ol className="list-decimal list-inside space-y-3">
                          {selectedArticle.steps.map((step) => (
                            <li key={step.step} className="text-muted-foreground">
                              <strong className="text-primary">{step.title}:</strong> {step.description}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    {selectedArticle.tips && selectedArticle.tips.length > 0 && (
                      // Use theme border and text classes
                      <div className="pt-4 border-t border-border/50 mt-4">
                        <h4 className="font-semibold text-primary mb-2">Tips:</h4>
                        <ul className="list-disc list-inside space-y-2">
                          {selectedArticle.tips.map((tip, index) => (
                            <li key={index} className="text-muted-foreground">{tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedArticle.videoEmbedUrl && (
                      // Use theme border and text classes
                      <div className="pt-4 border-t border-border/50 mt-4">
                        <h4 className="font-semibold text-primary mb-2">Video Guide:</h4>
                        <div className="aspect-video">
                          <iframe
                            width="100%"
                            height="100%"
                            src={selectedArticle.videoEmbedUrl}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="rounded"
                          ></iframe>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Use theme border class */}
                  <DialogFooter className="mt-auto pt-4 border-t border-border/50">
                    {/* Use theme classes/variant for Button */}
                    <Button onClick={closeArticleModal} variant="outline" className="border-border text-accent-foreground hover:bg-accent">Close</Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* Updated FAQ Section with Expand/Collapse */}
          {/* Use theme border and text classes */}
          <div className="space-y-2 pt-8 border-t border-border/50">
            <h3 className="text-xl font-semibold text-primary mb-4">Frequently Asked Questions</h3>
            {faqs.length > 0 ? (
              faqs.map((faq) => (
                // Use theme border class
                <div key={faq.id} className="border-b border-border/50 last:border-b-0">
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    // Use theme text classes
                    className="flex justify-between items-center w-full py-3 text-left text-accent-foreground hover:text-accent-foreground/80 focus:outline-none"
                  >
                    <span className="font-semibold">{faq.question}</span>
                    <ChevronDown
                      className={`h-5 w-5 transition-transform duration-200 ${
                        expandedFaq === faq.id ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {/* Content revealed when expanded */}
                  {expandedFaq === faq.id && (
                    // Use theme text class
                    <div className="pb-4 pt-1 text-muted-foreground text-sm">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))
            ) : (
              // Use theme text class
              <p className="text-muted-foreground">No FAQs available at the moment.</p>
            )}
          </div>


          {/* Existing Contact Support Section */}
          {/* Use theme border and text classes */}
          <div className="space-y-4 pt-8 border-t border-border/50">
            <h3 className="text-xl font-semibold text-primary">Contact Support</h3>
            <p className="text-muted-foreground">
              Can't find what you're looking for? Contact our support team directly. Please provide as much detail as possible about your issue.
            </p>
            {/* Use theme classes for anchor button */}
            <a
              href={mailtoLink}
              className="inline-flex items-center app-accent-purple-bg hover:app-accent-purple-hover text-white px-4 py-2 rounded transition-colors"
              style={{ textDecoration: 'none' }}
            >
              <Mail className="h-4 w-4 mr-2" />
              Email Support
            </a>
          </div>

        </CardContent>
      </Card>
    </div>
  );
};

export default SupportPage;
